import { Box, Grid, Typography, Link } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import { KeyValue, Query } from "../../interfaces";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import clsx from "clsx";
import {
  chunkArray,
  formatRepDate,
  reduceObj,
} from "../../common/services/Utilities";
import {
  htmlFootnote,
  labelFootnoteLookup,
  offBalanceSheetFootnotes,
  smallBusinessLoanFootnotes,
} from "./FinancialReportingUtilities";
import { standardComparisonOptions } from "../PeerGroup/PeerGroupUtilities";
import ModalDialog from "../ModalDialog";
import InterstateOfficesTable from "./InterstateOfficesTable";

interface Props {
  appState: Query;
  data: KeyValue[] | undefined;
  updateReportDownloadData: (downloadData: (string[] | number[])[]) => void;
  noInstName?: boolean;
  peerGroupsInfo?: KeyValue[] | null;
}
interface RenderTree {
  variable_ytd: string;
  variable_qtly: string;
  label: string;
  unit_type: string;
  fields: RenderTree[];
  sub_fields?: RenderTree[];
}

const useStyles = makeStyles(theme => ({
  highlight: {
    "& > .MuiTreeItem-content > .MuiTreeItem-label": {
      backgroundColor: "#fafafa",
    },
  },
  treeData: {
    "minWidth": 457,
    "position": "sticky",
    "left": 20,
    "zIndex": 1,
    "& > p": {
      "paddingTop": 2,
      "paddingBottom": 2,
      "marginLeft": -20,
      "paddingLeft": 20,
      "paddingRight": 5,
      "backgroundColor": "#F1F1F3",

      "&.highlight": {
        backgroundColor: "#dde7f4",
      },
    },
  },
  treeValue: {
    minWidth: 220,
    paddingRight: 8,
  },
  // styles to position the arrows and for first column with expandable rows
  expandList: {
    // indentation for each new level
    "& > ul": {
      "& .MuiTreeItem-iconContainer": { left: 14 },
      "& .treeData > p": { paddingLeft: 34 },
      "& li > ul": {
        "& .MuiTreeItem-iconContainer": { left: 31 },
        "& .treeData > p": { paddingLeft: 51 },
        "&  li > ul": {
          "& .MuiTreeItem-iconContainer": { left: 48 },
          "& .treeData > p": { paddingLeft: 68 },
          "&  li > ul": {
            "& .MuiTreeItem-iconContainer": { left: 65 },
            "& .treeData > p": { paddingLeft: 85 },
            "&  li > ul": {
              "& .MuiTreeItem-iconContainer": { left: 82 },
              "& .treeData > p": { paddingLeft: 102 },
              "&  li > ul": {
                "& .MuiTreeItem-iconContainer": { left: 99 },
                "& .treeData > p": { paddingLeft: 119 },
                "&  li > ul": {
                  "& .MuiTreeItem-iconContainer": { left: 116 },
                  "& .treeData > p": { paddingLeft: 136 },
                  "&  li > ul": {
                    "& .MuiTreeItem-iconContainer": { left: 131 },
                    "& .treeData > p": { paddingLeft: 151 },
                    "&  li > ul": {
                      "& .MuiTreeItem-iconContainer": { left: 148 },
                      "& .treeData > p": { paddingLeft: 168 },
                      "&  li > ul": {
                        "& .MuiTreeItem-iconContainer": { left: 165 },
                        "& .treeData > p": { paddingLeft: 185 },
                        "&  li > ul": {
                          "& .MuiTreeItem-iconContainer": { left: 182 },
                          "& .treeData > p": { paddingLeft: 202 },
                          "&  li > ul": {
                            "& .MuiTreeItem-iconContainer": { left: 199 },
                            "& .treeData > p": { paddingLeft: 219 },
                            "&  li > ul": {
                              "& .MuiTreeItem-iconContainer": { left: 216 },
                              "& .treeData > p": { paddingLeft: 236 },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  treeView: {
    "display": "table",
    "width": "100%",
    "& .MuiTreeItem-iconContainer": {
      position: "sticky",
      left: 0,
      zIndex: 2,
    },
    "& li#NAMEFULL_0": {
      "backgroundColor": theme.palette.secondary.main,
      "color": theme.palette.primary.main,
      "& p": {
        fontWeight: "bold",
      },
      "&:focus .treeData:first-child, &:hover .treeData:first-child": {
        "backgroundColor": "#c4d1e4",
        "& >p": {
          backgroundColor: "#c4d1e4",
        },
      },
      "& .treeData": {
        "minWidth": 457,
        "&:first-child": {
          "position": "sticky",
          "left": 20,
          "backgroundColor": "#d0dff6",
          "& > p": {
            height: "100%",
            backgroundColor: "#d0dff6",
            borderWidth: 0,
            position: "absolute",
          },
        },
      },
    },
    "& li": {
      "&:hover > .MuiTreeItem-content .treeData p, &:focus > .MuiTreeItem-content .treeData p": {
        backgroundColor: "#d4deea",
      },
      "& > .MuiTreeItem-content:focus .treeData p, & > .MuiTreeItem-content:hover .treeData p": {
        backgroundColor: "#d4deea",
      },
      "& ul": {
        marginLeft: 0,
      },
      "& > .MuiTreeItem-content": {
        cursor: "default",
        padding: 0,
      },
      "& .MuiTreeItem-label": {
        "padding": "0px",
        "& p": {
          color: theme.palette.primary.main,
        },
      },
      "&.MuiTreeItem-root:hover > .MuiTreeItem-content .MuiTreeItem-label, &.MuiTreeItem-root > div.Mui-focused > .MuiTreeItem-label, & div.Mui-focused > .MuiTreeItem-label .treeData > p": {
        filter: "brightness(.95)",
      },
    },
    "& li[aria-expanded]": {
      "& > .MuiTreeItem-content": {
        cursor: "pointer",
      },
    },
  },
  boldRow: {
    "& p": {
      fontWeight: "bold",
    },
  },
}));

export default function FinancialReportStructure({
  appState,
  data,
  updateReportDownloadData,
  noInstName,
  peerGroupsInfo,
}: Props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const arrayDownload: string[] = [];
  const reportStructure: RenderTree[] = appState.ui.reportStructure;
  const classes = useStyles();
  const [incomeBasis, setIncomeBasis] = React.useState(appState.ui.incomeBasis);
  const [unitType, setUnitType] = React.useState(appState.ui.unitType);
  const [reportPeriod, setReportPeriod] = React.useState(
    appState.filters.reportPeriod,
  );
  const [previousReportStructure, setPreviousReportStructure] = React.useState(
    reportStructure,
  );

  const [calculatedFootnote, setCalculatedFootnote] = React.useState(false);

  const [downloadData, setDownloadData] = React.useState<string[]>([]);
  const [openInterstateOffices, setOpenInterstateOffices] = React.useState(
    false,
  );

  React.useEffect(() => {
    if (JSON.stringify(downloadData) !== JSON.stringify(arrayDownload)) {
      setDownloadData(arrayDownload);
    }
  }, [downloadData, arrayDownload, reportPeriod.length]);
  React.useEffect(() => {
    if (
      JSON.stringify(previousReportStructure) !==
      JSON.stringify(appState.ui.reportStructure)
    ) {
      setPreviousReportStructure(appState.ui.reportStructure);
      setCalculatedFootnote(false);
    }
    if (
      JSON.stringify(incomeBasis) !== JSON.stringify(appState.ui.incomeBasis)
    ) {
      setIncomeBasis(appState.ui.incomeBasis);
      setCalculatedFootnote(false);
    }
    if (JSON.stringify(unitType) !== JSON.stringify(appState.ui.unitType)) {
      setUnitType(appState.ui.unitType);
      setCalculatedFootnote(false);
    }
    if (
      JSON.stringify(reportPeriod) !==
      JSON.stringify(appState.filters.reportPeriod)
    ) {
      setReportPeriod(appState.filters.reportPeriod);
      setCalculatedFootnote(false);
    }
  }, [
    appState.filters.reportPeriod,
    appState.ui.incomeBasis,
    appState.ui.reportStructure,
    appState.ui.reportType,
    appState.ui.unitType,
    incomeBasis,
    previousReportStructure,
    reportPeriod,
    unitType,
  ]);

  const footNoteAsterisk = (value: number, negative?: boolean) => {
    const newVal = negative ? value * -1 : value;
    return newVal > 0 && newVal < 0.005 ? "*" : "";
  };
  const formattedValNegative = (
    val: number,
    ratio: boolean,
    noUnit: boolean,
    noUnitDecimal: boolean,
  ) => {
    const type = ratio ? "%" : "$";
    const unit = noUnit || noUnitDecimal ? "" : type;
    if (!calculatedFootnote && footNoteAsterisk(val, true)) {
      setCalculatedFootnote(true);
    }

    return ratio && !noUnit
      ? isNaN(val)
        ? "N/A"
        : `-${(Math.round(val * -100) / 100).toFixed(
            2,
          )}${unit}${footNoteAsterisk(val, true)}`
      : `-${unit}${Math.round(val * -1).toLocaleString()}`;
  };
  const formattedVal = (
    val: number,
    ratio: boolean,
    noUnit: boolean,
    noUnitDecimal: boolean,
  ) => {
    const type = ratio ? "%" : "$";
    const unit = noUnit || noUnitDecimal ? "" : type;

    if (!calculatedFootnote && footNoteAsterisk(val)) {
      setCalculatedFootnote(true);
    }

    return ratio && !noUnit
      ? isNaN(val)
        ? "N/A"
        : `${(Math.round(val * 100) / 100).toFixed(2)}${unit}${footNoteAsterisk(
            val,
          )}`
      : `${unit}${Math.round(val).toLocaleString()}`;
  };
  function percentCalculation(
    calculations: KeyValue,
    resultData: KeyValue,
    ytd: boolean,
    noUnit: boolean,
    noUnitDecimal: boolean,
  ) {
    const incomeBasisCalc = ytd ? "variable_ytdr" : "variable_qtlyr";
    const numerator =
      calculations.variables &&
      calculations.variables.numerator[incomeBasisCalc].length > 0
        ? calculations.variables.numerator[incomeBasisCalc].map(
            (value: string) => resultData[value],
          )
        : [0];

    const reducer = (accumulator: number, curr: number) => accumulator + curr;

    // value will update to be the number of report types being displayed in the table//
    const denominator = 1;
    const ratio: number = (numerator.reduce(reducer) / denominator) * 100;
    if (Math.sign(ratio) === -1) {
      return formattedValNegative(ratio, true, noUnit, noUnitDecimal);
    } else {
      return formattedVal(ratio, true, noUnit, noUnitDecimal);
    }
  }

  const [footNoteVals, setFootNoteVals] = React.useState<string[]>([]);
  const formatDateArrays = ["ESTYMD", "INSDATE", "PROCDATE", "EFFDATE"];
  const yesOrNoValues = ["TRUST", "CB", "PARCERT"];
  let cfpbFlag = "";
  const treeData = (
    resultData: KeyValue,
    variable: string,
    value: string,
    calculation: KeyValue,
    ratio: boolean,
    ytd: boolean,
    noUnit: boolean,
    noUnitDecimal: boolean,
    pgcOnly: boolean,
    download: boolean,
    index: number,
    structureVariable: boolean,
  ) => {
    if (value) {
      return value;
    } else if (variable && variable.includes(",")) {
      const variableArray = variable.split(",");
      const values = variableArray.map((val: string) => {
        return resultData[val];
      });
      return values.join(", ");
    } else if (formatDateArrays.includes(variable)) {
      return resultData[variable] && resultData[variable] !== undefined
        ? formatRepDate(resultData[variable])
        : "";
    } else if (appState.ui.reportType === "demographics" || structureVariable) {
      if (variable === "ACTIVE") {
        return resultData[variable] === 1 ? "Active" : "Inactive";
      } else if (variable === "RSSDID" && !resultData[variable]) {
        return resultData.FED_RSSD;
      } else if (
        variable === "SPECGRPDESC" &&
        !resultData[variable] &&
        resultData.SPECGRPN
      ) {
        return resultData.SPECGRPN;
      } else if (
        variable === "STCNTY" &&
        !isNaN(resultData[variable]) &&
        resultData.COUNTY
      ) {
        return resultData.COUNTY;
      } else if (yesOrNoValues.includes(variable)) {
        return Number(resultData[variable]) === 1 ? "Yes" : "No";
      } else if (variable === "CFPBFLAG") {
        if (index === 0) {
          cfpbFlag = Number(resultData[variable]) === 1 ? "Yes" : "No";
          return cfpbFlag;
        }
        return cfpbFlag;
      } else if (variable === "MUTUAL" && Number(resultData[variable]) === 1) {
        return "Mutual";
      } else if (variable === "MUTUAL" && Number(resultData[variable]) !== 1) {
        return "Stock";
      } else if (variable === "STMULT") {
        return resultData[variable] === "1" ? (
          download ? (
            "Yes"
          ) : (
            <Link
              component="button"
              variant="body2"
              onClick={() => {
                setOpenInterstateOffices(true);
                index && setCurrentIndex(index);
              }}
            >
              Yes
            </Link>
          )
        ) : (
          "No"
        );
      } else return resultData[variable];
    } else if (variable === "TOTAL") {
      return resultData.count !== undefined ? resultData.count : 1;
    } else if (variable === "TBD") {
      return "TBD";
    } else if (calculation) {
      if (calculation.type === "percentage") {
        return percentCalculation(
          calculation,
          resultData,
          ytd,
          noUnit,
          noUnitDecimal,
        );
      } else {
        return "N/A";
      }
    } else if (
      (!resultData[variable] && resultData[variable] !== 0) ||
      pgcOnly
    ) {
      return "N/A";
    } else if (isNaN(resultData[variable])) {
      return resultData[variable];
    } else if (Math.sign(Number(resultData[variable])) === -1) {
      return formattedValNegative(
        resultData[variable],
        ratio,
        noUnit,
        noUnitDecimal,
      );
    } else {
      return formattedVal(resultData[variable], ratio, noUnit, noUnitDecimal);
    }
  };

  const dataVar = (nodes: KeyValue, index: number) =>
    incomeBasis[index] === "Qtly" && unitType[index] === "$"
      ? nodes.variable_qtly
      : incomeBasis[index] === "Qtly" && unitType[index] === "%"
      ? nodes.variable_qtlyr
      : incomeBasis[index] === "YTD" && unitType[index] === "%"
      ? nodes.variable_ytdr
      : nodes.variable_ytd;
  // formats the top layer data with correct spacing showing report period, income basis, and unit type above each column of data
  const peerGroups =
    appState.ui.peerGroupNames &&
    appState.ui.peerGroupNames.map((group: string, index: number) => [
      "",
      peerGroupsInfo &&
      peerGroupsInfo[index].title &&
      peerGroupsInfo[index].title.includes("FDIC Cert")
        ? "Cert"
        : "Peer Group",
      group,
    ]);

  const downloadReportPeriod = reportPeriod.map((report: string) => [
    "",
    "Report Period",
    report,
  ]);
  const downloadIncomeExpense = incomeBasis.map((income: string) => [
    "",
    "Income Basis",
    income,
  ]);
  const downloadUnitType = unitType.map((unit: string) => [
    "",
    "Unit Type",
    unit,
  ]);

  let downloadPush = ["", ...[].concat.apply([], downloadReportPeriod)];

  if (appState.ui.reportType !== "demographics") {
    downloadPush = [
      ...downloadPush,
      "",
      ...[].concat.apply([], downloadIncomeExpense),
      "",
      ...[].concat.apply([], downloadUnitType),
    ];
  } else {
    downloadPush = [
      "",
      "",
      "Current Data as of",
      data && data[0] ? data[0].RUNDATE : "",
      ...[].concat.apply([], downloadReportPeriod),
    ];
  }

  if (peerGroups) {
    downloadPush = [
      ...["", ...[].concat.apply([], peerGroups)],
      ...downloadPush,
    ];
  }
  arrayDownload.push(...downloadPush);
  // This resolves the issue with duplicate ID's in the table if the variables are TBD
  let TBDCounter = 0;

  const renderTree = (
    nodes: KeyValue,
    int: number,
    hideInt?: boolean,
    subFootnote?: string,
  ) => {
    let footNoteRef = "";
    if (nodes.footnote) {
      footNoteRef = nodes.footnote.key;
    } else if (nodes.ftnKey) {
      footNoteRef = nodes.ftnKey;
    } else if (subFootnote) {
      footNoteRef = subFootnote;
    }

    if (
      appState.ui.reportType === "customResults" &&
      nodes.footNote &&
      !footNoteVals.includes(nodes.footNote)
    ) {
      setFootNoteVals([...footNoteVals, nodes.footNote]);
    } else if (
      appState.ui.reportType !== "customResults" &&
      footNoteVals.length > 0
    ) {
      setFootNoteVals([]);
    }
    const treeItemLabel =
      !hideInt && int ? `${int}. ${nodes.label}` : `${nodes.label}`;

    const footnote = footNoteRef ? ` (footnote: ${footNoteRef}` : "";
    // pushes the treeItemLabel to the array for the download
    arrayDownload.push(`${treeItemLabel}${footnote}`);
    // if data exists, it pushes the formatted data for each item
    if (data) {
      data.forEach((val: KeyValue, index: number) => {
        const variable = dataVar(nodes, index);
        return arrayDownload.push(
          "",
          treeData(
            val,
            variable,
            variable,
            nodes.calculation,
            nodes.unitType ? nodes.unitType === "%" : unitType[index] === "%",
            incomeBasis[index] === "YTD",
            nodes.no_unit,
            nodes.no_unit_decimal,
            nodes.pgc_only && !appState.ui.peerGroups,
            true,
            index,
            nodes.label?.includes("(Structure)"),
          ),
          treeData(
            val,
            variable,
            nodes.value,
            nodes.calculation,
            nodes.unitType ? nodes.unitType === "%" : unitType[index] === "%",
            incomeBasis[index] === "YTD",
            nodes.no_unit,
            nodes.no_unit_decimal,
            nodes.pgc_only && !appState.ui.peerGroups,
            true,
            index,
            nodes.label?.includes("(Structure)"),
          ),
        );
      });
    }

    if (
      !nodes.variable_qtly &&
      !nodes.variable_ytd &&
      !nodes.variable_ytdr &&
      !nodes.variable_qtlyr
    ) {
      TBDCounter = TBDCounter + 1;
    }
    let YTDvar = nodes.variable_ytd || nodes.variable_ytdr;
    if (nodes.calculation) {
      const numerator = nodes.calculation.variables.numerator;
      if (numerator.variable_ytd) {
        YTDvar = nodes.calculation.variables.numerator.variable_ytd[0];
      } else {
        YTDvar = nodes.calculation.variables.numerator.variable_ytdr[0];
      }
    }
    const QtlyVar = nodes.variable_qtly || nodes.variable_qtlyr;
    if (!YTDvar && !QtlyVar) {
      TBDCounter = TBDCounter + 1;
    }
    const idVar = YTDvar ? `${YTDvar}_${int}` : `TBD_${TBDCounter}`;

    const expandableClass =
      Array.isArray(nodes.sub_fields) && !hideInt
        ? classes.expandList
        : undefined;
    const cellHighlight = int % 2 ? "highlight" : "";
    const treeItemClass =
      int % 2 ? clsx(classes.highlight, expandableClass) : expandableClass;

    const subFieldFootnotes =
      nodes.footnote && nodes.footnote.all_sub_fields
        ? nodes.footnote.key
        : undefined;

    const subTreeItem = Array.isArray(nodes.sub_fields)
      ? nodes.sub_fields.map((sub_node: KeyValue, subInt: number) => {
          return renderTree(
            sub_node,
            subInt,
            true,
            subFieldFootnotes || subFootnote,
          );
        })
      : null;

    const boldRowsClass =
      ["DRLNLS", "CRLNLS", "NETINC"].includes(nodes.variable_ytd) &&
      appState.ui.reportType !== "customResults"
        ? classes.boldRow
        : undefined;

    return data ? (
      <TreeItem
        className={clsx(treeItemClass, boldRowsClass)}
        id={idVar}
        collapseIcon={<ExpandMoreIcon />}
        expandIcon={<ChevronRightIcon />}
        tabIndex={0}
        TransitionProps={{
          exit: false,
          enter: false,
        }}
        key={`${idVar}_${int}`}
        nodeId={`${idVar}_${int}`}
        label={
          <Grid container style={{ flexWrap: "nowrap" }}>
            <Grid item xs={8} className={clsx(classes.treeData, "treeData")}>
              <Typography
                variant={"body1"}
                className={cellHighlight}
                style={{ fontSize: 16, height: "100%" }}
              >
                {treeItemLabel}
                <sup>{footNoteRef}</sup>
              </Typography>
            </Grid>

            {data.map((val: KeyValue, index: number) => {
              return (
                <Grid
                  key={`${val}_${index}`}
                  item
                  xs={4}
                  className={clsx("tree-value", classes.treeValue)}
                >
                  <Typography
                    variant="body1"
                    style={{ textAlign: "right", fontSize: 16 }}
                  >
                    {treeData(
                      val,
                      dataVar(nodes, index),
                      nodes.value,
                      nodes.calculation,
                      nodes.unitType
                        ? nodes.unitType === "%"
                        : unitType[index] === "%",
                      incomeBasis[index] === "YTD",
                      nodes.no_unit,
                      nodes.no_unit_decimal,
                      nodes.pgc_only && !appState.ui.peerGroups,
                      false,
                      index,
                      nodes.label?.includes("(Structure)"),
                    )}
                  </Typography>
                </Grid>
              );
            })}
          </Grid>
        }
      >
        {subTreeItem}
      </TreeItem>
    ) : (
      undefined
    );
  };

  const footNoteOrder = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

  const sortedFootnote: KeyValue = reduceObj(
    footNoteVals.map((footnote: string) => {
      // splits to get the key to define order
      return {
        key: footNoteOrder.indexOf(footnote.split(" ")[0]),
        value: footnote,
      };
    }),
  );

  const chunkNumber =
    appState.ui.reportType !== "demographics"
      ? reportPeriod.length
      : reportPeriod.length + 1;

  const structureDownload = React.useMemo(() => {
    const downloadLabelFootnote =
      appState.ui.reportType && footNoteVals.length === 0
        ? [
            ...labelFootnoteLookup(
              appState.ui.reportType,
              true,
            ).map((footnote: string) => [footnote]),
          ]
        : Object.values(sortedFootnote).length > 0
        ? [
            ...Object.values(sortedFootnote).map((footnote: string) => [
              footnote === "8 "
                ? `8. ${smallBusinessLoanFootnotes(true).downloadText}`
                : footnote === "9 "
                ? `9. ${offBalanceSheetFootnotes(true).downloadText}`
                : `${footnote.split(" ")[0]}. ${footnote
                    .split(" ")
                    .slice(1)
                    .join(" ")}`,
            ]),
          ]
        : [];
    const reportInfo =
      appState.ui.comparisonType && appState.ui.reportStructure
        ? [
            [
              `${
                appState.ui.reportType === "customResults"
                  ? "Custom Results"
                  : appState.ui.reportStructure[0].label
              }`,
            ],
            [
              `Comparison Type: ${
                standardComparisonOptions.find(
                  (comparison: KeyValue) =>
                    appState.ui.comparisonType === comparison.value,
                )?.label
              }`,
            ],
          ]
        : appState.ui.reportStructure && data && data[0].CERT
        ? [
            [
              `${
                appState.ui.reportType === "customResults"
                  ? "Custom Results"
                  : appState.ui.reportStructure[0].label
              } - Cert #: ${data[0].CERT}`,
            ],
          ]
        : [[""]];
    const footNoteDownload = calculatedFootnote
      ? ["* Calculated value rounds to 0.00%"]
      : [""];
    return [
      ...reportInfo,
      [
        "Dollar Figures in Thousands (USD) or Percent of Average Assets Annualized",
      ],
      // chunks array by number of columns. is multiplied by 3 to accomodate for the variable and additional space for each report and the + 1 is for the label for each row.
      ...chunkArray(downloadData, chunkNumber * 3 + 1),
      [""],
      footNoteDownload,
      ...downloadLabelFootnote,
    ];
  }, [
    chunkNumber,
    downloadData,
    appState.ui,
    calculatedFootnote,
    data,
    footNoteVals.length,
    sortedFootnote,
  ]);
  const [
    previousStructureDownload,
    setPreviousStructureDownload,
  ] = React.useState<(string[] | number[])[]>([]);

  React.useEffect(() => {
    if (
      JSON.stringify(previousStructureDownload) !==
      JSON.stringify(structureDownload)
    ) {
      updateReportDownloadData(structureDownload);
      setPreviousStructureDownload(structureDownload);
    }
  }, [previousStructureDownload, structureDownload, updateReportDownloadData]);

  const [currentIndex, setCurrentIndex] = React.useState(0);
  const demographicsModal = () => {
    return data ? (
      <ModalDialog
        title={
          data[currentIndex].NAMEFULL
            ? data[currentIndex].NAMEFULL
            : data[currentIndex].NAME
        }
        subtitle="has offices in the following states that can accept FDIC-insured deposits"
        display={openInterstateOffices}
        setDisplay={setOpenInterstateOffices}
        maxWidth="lg"
      >
        <InterstateOfficesTable
          cert={data[currentIndex].CERT}
          risdate={data[currentIndex].RISDATE}
        />
      </ModalDialog>
    ) : (
      <></>
    );
  };
  function reportReturn(reportData: KeyValue | undefined) {
    if (reportData) {
      return (
        <>
          {JSON.stringify(incomeBasis) !==
          JSON.stringify(appState.ui.incomeBasis) ? (
            undefined
          ) : (
            <>
              {demographicsModal()}

              <TreeView disableSelection className={classes.treeView}>
                {reportStructure.map(report => {
                  if (noInstName) {
                    delete report.fields[0];
                  }
                  return report.fields.map((fields: KeyValue, int: number) => {
                    return renderTree(fields, int);
                  });
                })}
              </TreeView>
            </>
          )}
          <Grid container style={{ position: "sticky", left: 0 }}>
            <Grid item xs={8}>
              {labelFootnoteLookup(appState.ui.reportType)}
              <>
                {Object.values(sortedFootnote).length > 0 &&
                  Object.values(sortedFootnote).map(
                    (value: string, index: number) => {
                      const footnoteText =
                        value === "8 " ? (
                          <>8. {smallBusinessLoanFootnotes(true).text}</>
                        ) : value === "9 " ? (
                          <>9. {offBalanceSheetFootnotes(true).text}</>
                        ) : (
                          `${value.split(" ")[0]}. ${value
                            .split(" ")
                            .slice(1)
                            .join(" ")}`
                        );
                      return htmlFootnote(
                        footnoteText,
                        index,
                        "sorted-footnote",
                      );
                    },
                  )}
              </>
            </Grid>
            <Grid item xs={4}>
              {calculatedFootnote ? (
                <Typography
                  variant="body2"
                  color="primary"
                  style={{
                    marginTop: "1.5rem",
                    // paddingBottom: ".75rem",
                    float: "right",
                  }}
                >
                  *Calculated value rounds to 0.00%
                </Typography>
              ) : (
                undefined
              )}
              {appState.ui.peerGroups &&
              appState.ui.peerGroups.length > 0 &&
              appState.ui.reportType === "performance-and-condition-ratios" ? (
                <Typography
                  variant="body2"
                  color="primary"
                  style={{
                    marginTop: "-.5rem",
                    paddingBottom: ".75rem",
                    float: "right",
                  }}
                >
                  **Values Currently In Development
                </Typography>
              ) : (
                undefined
              )}
            </Grid>
          </Grid>
        </>
      );
    } else {
      return (
        <Box marginTop={3} textAlign="center" id="no-results-message">
          <Typography variant="h2" style={{ paddingBottom: 10 }}>
            No Results
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            style={{ paddingBottom: 0 }}
          >
            No data is available for this Institution at the time frame
            specified, please select a different reporting period.
          </Typography>
          <Typography variant="body2" color="primary">
            You can also review the Institution’s Details for history, or
            research with Events & Changes, to find out more.
          </Typography>
        </Box>
      );
    }
  }
  return reportStructure && appState.ui.reportType ? reportReturn(data) : null;
}
